import { useContext, useMemo, useState } from 'react'
import { CartesianGrid, Legend, Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { format } from 'date-fns'
import { PreferencesContext } from './PreferencesContext'
import { formatDate, shiftArray } from '../utils'
import { Series } from '../types'
import './Chart.css'

const DEFAULT_STROKES = ['#3C82F6', '#12B981', '#F6A63C', '#8A4CF6', '#6B7C93', '#E28F8F']

export interface ChartProps {
    series: Series[]
    formatter?: (value: number) => string
    axisFormatter?: (value: number) => string
    strokeIndex?: number
    maxHeight?: number
    dateFormat?: string
    showLegend?: boolean
    showAxes?: boolean
    onZoom?: (start: Date, end: Date) => void
}

/**
 * Merge multiple series into a single dataset for recharts.
 * Each row becomes { date, [label1]: count, [label2]: count, ... }
 * so that all lines share the same data array and tooltips work correctly.
 */
function mergeSeriesData(series: Series[]): Array<Record<string, number>> {
    const byDate = new Map<number, Record<string, number>>()

    for (const s of series) {
        for (const point of s.data) {
            const date = typeof point.date === 'number'
                ? point.date
                : typeof point.date === 'string'
                    ? Date.parse(point.date)
                    : point.date.getTime()

            if (!byDate.has(date)) {
                byDate.set(date, { date })
            }
            byDate.get(date)![s.label] = point.count
        }
    }

    return Array.from(byDate.values()).sort((a, b) => a.date - b.date)
}

export default function Chart({
    series,
    formatter = (value) => value.toLocaleString(),
    axisFormatter,
    strokeIndex = 0,
    maxHeight = 250,
    dateFormat = 'h:mm aa',
    showLegend = true,
    showAxes = true,
    onZoom,
}: ChartProps) {
    const tickFormatter = axisFormatter ?? formatter
    const strokes = shiftArray(DEFAULT_STROKES, strokeIndex)
    const [preferences] = useContext(PreferencesContext)
    const data = useMemo(() => mergeSeriesData(series), [series])

    // Drag-to-zoom state
    const [dragStart, setDragStart] = useState<number | null>(null)
    const [dragEnd, setDragEnd] = useState<number | null>(null)

    const handleMouseDown = (e: any) => {
        if (!onZoom || !e?.activeLabel) return
        setDragStart(e.activeLabel)
        setDragEnd(null)
    }

    const handleMouseMove = (e: any) => {
        if (!dragStart || !e?.activeLabel) return
        setDragEnd(e.activeLabel)
    }

    const handleMouseUp = () => {
        if (!dragStart || !dragEnd || !onZoom) {
            setDragStart(null)
            setDragEnd(null)
            return
        }

        const left = Math.min(dragStart, dragEnd)
        const right = Math.max(dragStart, dragEnd)

        // Only zoom if the selection spans a meaningful range
        if (right - left > 0) {
            onZoom(new Date(left), new Date(right))
        }

        setDragStart(null)
        setDragEnd(null)
    }

    return (
        <ResponsiveContainer width="100%" height={maxHeight}>
            <LineChart
                data={data}
                margin={showAxes
                    ? { top: 5, left: 0, bottom: 5, right: 0 }
                    : { top: 2, left: 0, bottom: 2, right: 0 }
                }
                onMouseDown={onZoom ? handleMouseDown : undefined}
                onMouseMove={onZoom ? handleMouseMove : undefined}
                onMouseUp={onZoom ? handleMouseUp : undefined}
            >
                <XAxis
                    dataKey="date"
                    tickFormatter={date => format(date, dateFormat)}
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    tick={showAxes ? { fontSize: 12 } : false}
                    tickCount={15}
                    tickMargin={8}
                    hide={!showAxes}
                />
                <YAxis
                    tick={showAxes ? { fontSize: 12 } : false}
                    tickFormatter={count => tickFormatter(count)}
                    width={showAxes ? undefined : 0}
                    hide={!showAxes}
                />
                <CartesianGrid vertical={false} horizontal={showAxes} />
                <Tooltip
                    contentStyle={{
                        backgroundColor: 'var(--color-background)',
                        border: '1px solid var(--color-grey)',
                    }}
                    labelFormatter={(date: number) => formatDate(preferences, date)}
                    formatter={(value: any, name) => [`${name}: ${formatter(value)}`]}
                />
                {showLegend && <Legend />}
                {series.map((s, index) => (
                    <Line
                        type="monotone"
                        dataKey={s.label}
                        key={s.label}
                        stroke={strokes[index]}
                        dot={false}
                    />
                ))}
                {dragStart && dragEnd && (
                    <ReferenceArea
                        x1={dragStart}
                        x2={dragEnd}
                        strokeOpacity={0.3}
                        fill="var(--color-primary)"
                        fillOpacity={0.1}
                    />
                )}
            </LineChart>
        </ResponsiveContainer>
    )
}
